import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { useLocation, useHistory } from 'react-router-dom';
import ProjectContext from '../context/ProjectContext';
import { Card, Tabs, Tab, Badge, Button, Spinner, Modal, Form } from 'react-bootstrap';
import { formatCurrency } from '../utils/formatters';
import moment from 'moment';

// Sub-components
import { ProfitAndLossReport } from '../components/reports/ProfitAndLossReport';
import { BalanceSheetReport } from '../components/reports/BalanceSheetReport';
import { CashFlowReport } from '../components/reports/CashFlowReport';
import { AISummaryReport } from '../components/reports/AISummaryReport';

const Reports = () => {
  const { activeProject } = useContext(ProjectContext);
  const location = useLocation();
  const history = useHistory();

  // Get active tab from URL query parameter (defaults to 'pnl')
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get('tab') || 'pnl';

  const handleTabChange = (key) => {
    queryParams.set('tab', key);
    history.replace({ search: queryParams.toString() });
  };
  
  // State for selectors default helpers
  const getStartOfMonthStr = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  };
  const getEndOfMonthStr = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  };

  // Retrieve persisted report filter dates if available, scoped to project
  const getPersistedDate = (key, fallback) => {
    if (!activeProject?._id) return fallback;
    return localStorage.getItem(`reports_${key}_${activeProject._id}`) || fallback;
  };

  const [startDate, setStartDate] = useState(getStartOfMonthStr());
  const [endDate, setEndDate] = useState(getEndOfMonthStr());
  const [compareDates, setCompareDates] = useState([]);

  // Sync date selectors from localStorage on active project changes
  useEffect(() => {
    if (activeProject?._id) {
      setStartDate(localStorage.getItem(`reports_startDate_${activeProject._id}`) || getStartOfMonthStr());
      setEndDate(localStorage.getItem(`reports_endDate_${activeProject._id}`) || getEndOfMonthStr());
      const persistedCompare = localStorage.getItem(`reports_compareDates_${activeProject._id}`);
      setCompareDates(persistedCompare ? JSON.parse(persistedCompare) : []);
    }
  }, [activeProject?._id]);

  // Persist dates in localStorage when they change
  useEffect(() => {
    if (activeProject?._id) {
      localStorage.setItem(`reports_startDate_${activeProject._id}`, startDate);
      localStorage.setItem(`reports_endDate_${activeProject._id}`, endDate);
      localStorage.setItem(`reports_compareDates_${activeProject._id}`, JSON.stringify(compareDates));
    }
  }, [startDate, endDate, compareDates, activeProject?._id]);
  
  // Computed values to fix runtime ReferenceErrors
  const selectedMonth = new Date(startDate).getMonth() + 1;
  const selectedYear = new Date(startDate).getFullYear();

  // State for report data
  const [pnl, setPnl] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [aiReport, setAiReport] = useState(null);
  const [auditReport, setAuditReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);

  // Streaming logs state
  const [aiLogs, setAiLogs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // Stream Refs
  const aiEventSourceRef = useRef(null);
  const auditEventSourceRef = useRef(null);
  const consoleEndRef = useRef(null);

  // Clean up streams on unmount
  useEffect(() => {
    return () => {
      if (aiEventSourceRef.current) aiEventSourceRef.current.close();
      if (auditEventSourceRef.current) auditEventSourceRef.current.close();
    };
  }, []);

  // Auto-scroll console to bottom when new logs arrive
  useEffect(() => {
    if (consoleEndRef.current) {
      consoleEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [aiLogs, auditLogs]);

  // Share Modal State
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(false);

  useEffect(() => {
    if (activeProject) {
      fetchReports();
    }
  }, [activeProject, startDate, endDate, compareDates]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = { startDate, endDate };
      const bsParams = { endDate };
      if (compareDates.length > 0) {
        bsParams.compareDates = compareDates.join(',');
      }
      const aiParams = { month: selectedMonth, year: selectedYear };

      const [pnlRes, bsRes, cfRes, aiRes, auditRes, accountsRes] = await Promise.all([
        axios.get(`/api/projects/${activeProject._id}/reports/pnl`, { params }),
        axios.get(`/api/projects/${activeProject._id}/reports/balance-sheet`, { params: bsParams }),
        axios.get(`/api/projects/${activeProject._id}/reports/cash-flow`, { params }),
        axios.get(`/api/projects/${activeProject._id}/reports/ai-summary`, { params: { ...aiParams, type: 'monthly_summary' } }),
        axios.get(`/api/projects/${activeProject._id}/reports/ai-summary`, { params: { ...aiParams, type: 'final_audit' } }),
        axios.get(`/api/projects/${activeProject._id}/accounts`)
      ]);
      
      setPnl(pnlRes.data);
      setBalanceSheet(bsRes.data);
      setCashFlow(cfRes.data);
      setAccounts(accountsRes.data);
      
      const aiRep = aiRes.data[0] || null;
      const auditRep = auditRes.data[0] || null;
      setAiReport(aiRep);
      setAuditReport(auditRep);

      if (aiRep && (aiRep.status === 'pending' || aiRep.status === 'processing')) {
        connectLogStream(aiRep._id, 'monthly_summary');
      }
      if (auditRep && (auditRep.status === 'pending' || auditRep.status === 'processing')) {
        connectLogStream(auditRep._id, 'final_audit');
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const connectLogStream = (reportId, type) => {
    if (type === 'final_audit') {
      if (auditEventSourceRef.current) auditEventSourceRef.current.close();
      setAuditLogs([]);
    } else {
      if (aiEventSourceRef.current) aiEventSourceRef.current.close();
      setAiLogs([]);
    }

    const token = localStorage.getItem('token');
    const eventSource = new EventSource(
      `/api/projects/${activeProject._id}/reports/ai-summary/${reportId}/stream?token=${token}`
    );

    if (type === 'final_audit') {
      auditEventSourceRef.current = eventSource;
    } else {
      aiEventSourceRef.current = eventSource;
    }

    let isClosedCleanly = false;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'log') {
          if (type === 'final_audit') {
            setAuditLogs((prev) => [...prev, data.data]);
          } else {
            setAiLogs((prev) => [...prev, data.data]);
          }
        } else if (data.type === 'status') {
          const statusMsg = `[SYSTEM]: ${data.data.message}`;
          if (type === 'final_audit') {
            setAuditLogs((prev) => [...prev, statusMsg]);
          } else {
            setAiLogs((prev) => [...prev, statusMsg]);
          }
        } else if (data.type === 'complete') {
          if (type === 'final_audit') {
            setAuditReport(data.data);
          } else {
            setAiReport(data.data);
          }
          isClosedCleanly = true;
          eventSource.close();
        } else if (data.type === 'error' || data.type === 'failed') {
          const errorMsg = `[ERROR]: ${data.data.message || 'Generation failed'}`;
          if (type === 'final_audit') {
            setAuditLogs((prev) => [...prev, errorMsg]);
          } else {
            setAiLogs((prev) => [...prev, errorMsg]);
          }
          isClosedCleanly = true;
          eventSource.close();
        }
      } catch (err) {
        console.error('Error parsing SSE event:', err);
      }
    };

    eventSource.onerror = (err) => {
      if (isClosedCleanly) return;
      console.error('EventSource failed:', err);
      const errorMsg = `[SYSTEM ERROR]: Connection lost. Polling status instead...`;
      if (type === 'final_audit') {
        setAuditLogs((prev) => [...prev, errorMsg]);
      } else {
        setAiLogs((prev) => [...prev, errorMsg]);
      }
      eventSource.close();
      startPolling(type);
    };

    return eventSource;
  };

  const triggerAiSummary = async (type = 'monthly_summary') => {
    if (type === 'final_audit') setAuditLoading(true);
    else setAiLoading(true);

    try {
        const res = await axios.post(`/api/projects/${activeProject._id}/reports/ai-summary/trigger`, {
            month: selectedMonth,
            year: selectedYear,
            type
        });
        
        if (type === 'final_audit') {
            setAuditReport(res.data);
            connectLogStream(res.data._id, 'final_audit');
        } else {
            setAiReport(res.data);
            connectLogStream(res.data._id, 'monthly_summary');
        }
    } catch (err) {
        console.error('Error triggering report:', err);
        alert('Failed to trigger report generation');
    } finally {
        setAuditLoading(false);
        setAiLoading(false);
    }
  };

  const startPolling = (type) => {
      const interval = setInterval(async () => {
          try {
              const res = await axios.get(`/api/projects/${activeProject._id}/reports/ai-summary`, {
                  params: { month: selectedMonth, year: selectedYear, type }
              });
              const report = res.data[0];
              if (report) {
                  if (type === 'final_audit') setAuditReport(report);
                  else setAiReport(report);

                  if (report.status === 'completed' || report.status === 'failed') {
                      clearInterval(interval);
                  }
              }
          } catch (err) {
              console.error('Polling error:', err);
              clearInterval(interval);
          }
      }, 5000);
  };

  const handleShare = async () => {
    if (!aiReport || aiReport.status !== 'completed') return;
    setShareLoading(true);
    try {
        const res = await axios.post(`/api/public/reports/${aiReport._id}/share`);
        const updatedShareData = {
            ...res.data,
            isRevoked: false
        };
        setShareData(updatedShareData);
        setAiReport(prev => ({ 
            ...prev, 
            sharedToken: updatedShareData.token,
            accessCode: updatedShareData.accessCode,
            isRevoked: false 
        }));
        setShowShareModal(true);
    } catch (err) {
        console.error('Error sharing report:', err);
        alert('Failed to generate share link');
    } finally {
        setShareLoading(false);
    }
  };

  const handleRevoke = async () => {
      if (!aiReport || !window.confirm('Are you sure you want to revoke this link? The client will no longer be able to access it.')) return;
      setRevokeLoading(true);
      try {
          await axios.delete(`/api/public/reports/${aiReport._id}/share`);
          setShareData(prev => ({ ...prev, isRevoked: true }));
          setAiReport(prev => ({ ...prev, isRevoked: true, sharedToken: null }));
          alert('Shared link has been revoked.');
      } catch (err) {
          console.error('Error revoking link:', err);
          alert('Failed to revoke link');
      } finally {
          setRevokeLoading(false);
      }
  };

  const handleAdminDownload = async () => {
    if (!aiReport || aiReport.status !== 'completed') return;
    try {
        const res = await axios.post(`/api/projects/${activeProject._id}/reports/ai-summary/${aiReport._id}/token`);
        const { downloadToken } = res.data;
        window.open(`http://localhost:5001/api/projects/${activeProject._id}/reports/ai-summary/${aiReport._id}/download?token=${downloadToken}`, '_blank');
    } catch (err) {
        console.error('Error generating download token:', err);
        alert('Failed to authorize download');
    }
  };
  
  const handleExcelExport = () => {
    const baseUrl = 'http://localhost:5001';
    const endpoint = activeTab === 'pnl' 
      ? `${baseUrl}/api/projects/${activeProject._id}/reports/pnl/export/excel`
      : `${baseUrl}/api/projects/${activeProject._id}/reports/balance-sheet/export/excel`;
    
    const params = activeTab === 'pnl' 
      ? `?startDate=${startDate}&endDate=${endDate}`
      : `?endDate=${endDate}`;
      
    const token = localStorage.getItem('token');
    window.open(`${endpoint}${params}&token=${token}`, '_blank');
  };

  if (!activeProject) return <div className="p-5 text-center">Please select or create a project first.</div>;

  return (
    <div className="pb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-0">Financial Reports</h1>
          <p className="text-muted small mb-0">Foundational statements for {activeProject.name}</p>
        </div>
        
        {/* Period Selector & Global Actions */}
        <div className="d-flex align-items-center">
            {aiReport && aiReport.status === 'completed' && (
                <div className="mr-3 border-right pr-3 d-flex align-items-center">
                    <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="mr-2"
                        onClick={handleAdminDownload}
                        title="Download multi-page professional PDF"
                    >
                        <i className="fas fa-file-pdf mr-2"></i>
                        PDF
                    </Button>
                    <Button 
                        variant="outline-success" 
                        size="sm" 
                        className="mr-3"
                        onClick={handleShare}
                        disabled={shareLoading}
                        title="Generate secure shared link for client"
                    >
                        <i className="fas fa-share-alt mr-2"></i>
                        {shareLoading ? '...' : 'Share'}
                    </Button>

                    {/* Delivery Status Indicator */}
                    <div className="text-right">
                        {aiReport.isRevoked ? (
                            <Badge variant="danger" className="small" style={{ fontSize: '0.7rem' }}>Revoked</Badge>
                        ) : aiReport.sharedToken ? (
                            <Badge variant="success" className="small" style={{ fontSize: '0.7rem' }}>Link Active</Badge>
                        ) : (
                            <Badge variant="light" className="small border text-muted" style={{ fontSize: '0.7rem' }}>Internal Only</Badge>
                        )}
                    </div>
                </div>
            )}
            {(activeTab === 'pnl' || activeTab === 'balance-sheet') && (
                <div className="d-flex align-items-center mr-3">
                    <Button 
                        variant="outline-success" 
                        size="sm" 
                        className="mr-2"
                        onClick={handleExcelExport}
                        title="Export this report to Excel"
                    >
                        <i className="fas fa-file-excel mr-2"></i>
                        Excel
                    </Button>
                    <Button 
                        variant="outline-primary" 
                        size="sm" 
                        onClick={() => window.print()}
                        title="Print/Save report to PDF"
                    >
                        <i className="fas fa-file-pdf mr-2"></i>
                        PDF
                    </Button>
                </div>
            )}

            <div className="bg-white p-2 border rounded shadow-sm d-flex align-items-center">
                {activeTab !== 'balance-sheet' && (
                    <div className="d-flex align-items-center mr-2">
                        <span className="small text-muted mr-1 font-weight-bold" style={{ fontSize: '0.75rem' }}>From:</span>
                        <Form.Control 
                            type="date" 
                            size="sm" 
                            value={startDate} 
                            onChange={e => setStartDate(e.target.value)}
                            style={{ width: '135px', fontSize: '0.75rem' }}
                        />
                    </div>
                )}
                <div className="d-flex align-items-center">
                    <span className="small text-muted mr-1 font-weight-bold" style={{ fontSize: '0.75rem' }}>To:</span>
                    <Form.Control 
                        type="date" 
                        size="sm" 
                        value={endDate} 
                        onChange={e => setEndDate(e.target.value)}
                        style={{ width: '135px', fontSize: '0.75rem' }}
                    />
                </div>
            </div>
        </div>
      </div>

      {activeTab === 'balance-sheet' && (
        <div className="bg-white p-3 border rounded shadow-sm mb-4 d-flex align-items-center justify-content-between flex-wrap">
          <div className="d-flex align-items-center flex-wrap">
            <span className="small text-muted font-weight-bold mr-3" style={{ fontSize: '0.85rem' }}>
              <i className="fas fa-columns mr-1 text-primary"></i> Compare Periods:
            </span>
            {compareDates.map((dateStr, index) => (
              <div key={index} className="d-flex align-items-center mr-3 mb-2 mb-md-0">
                <Form.Control
                  type="date"
                  size="sm"
                  value={dateStr}
                  onChange={(e) => {
                    const newDates = [...compareDates];
                    newDates[index] = e.target.value;
                    setCompareDates(newDates);
                  }}
                  style={{ width: '135px', fontSize: '0.75rem', marginRight: '5px' }}
                />
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    const newDates = compareDates.filter((_, i) => i !== index);
                    setCompareDates(newDates);
                  }}
                  style={{ padding: '0.1rem 0.3rem', fontSize: '0.75rem' }}
                  title="Remove comparison"
                >
                  <i className="fas fa-times"></i>
                </Button>
              </div>
            ))}
            
            <Button
              variant="outline-primary"
              size="sm"
              className="mr-2 mb-2 mb-md-0"
              onClick={() => {
                // Find reference date (earliest comparison date or main endDate)
                const refDateStr = compareDates.length > 0 ? compareDates[compareDates.length - 1] : endDate;
                const refMoment = moment(refDateStr);
                // Subtract 1 month and get the end of that month
                const newDateStr = refMoment.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
                setCompareDates([...compareDates, newDateStr]);
              }}
            >
              <i className="fas fa-plus mr-1"></i> Add Month
            </Button>

            <Button
              variant="outline-secondary"
              size="sm"
              className="mb-2 mb-md-0"
              onClick={() => {
                const refDateStr = compareDates.length > 0 ? compareDates[compareDates.length - 1] : endDate;
                const refMoment = moment(refDateStr);
                // Subtract 1 quarter (3 months) and get end of that quarter's month
                const newDateStr = refMoment.subtract(3, 'months').endOf('month').format('YYYY-MM-DD');
                setCompareDates([...compareDates, newDateStr]);
              }}
            >
              <i className="fas fa-plus mr-1"></i> Add Quarter
            </Button>
          </div>
          {compareDates.length > 0 && (
            <Button
              variant="link"
              size="sm"
              className="text-danger p-0"
              onClick={() => setCompareDates([])}
            >
              Clear Comparison
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-5 bg-white border rounded shadow-sm my-4">
            <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
            <p className="text-muted mt-3 mb-0">Loading report data...</p>
        </div>
      ) : (
        <Tabs activeKey={activeTab} onSelect={handleTabChange} id="report-tabs" className="mb-4 custom-tabs">
          <Tab eventKey="pnl" title="Profit & Loss">
            {pnl && (
              <ProfitAndLossReport 
                pnl={pnl}
                startDate={startDate}
                endDate={endDate}
                activeProject={activeProject}
                accounts={accounts}
                formatCurrency={formatCurrency}
              />
            )}
          </Tab>

          <Tab eventKey="balance-sheet" title="Balance Sheet">
            {balanceSheet && (
              <BalanceSheetReport 
                balanceSheet={balanceSheet}
                endDate={endDate}
                activeProject={activeProject}
                accounts={accounts}
                formatCurrency={formatCurrency}
              />
            )}
          </Tab>

          <Tab eventKey="cash-flow" title="Statement of Cash Flows">
            {cashFlow && (
              <CashFlowReport 
                cashFlow={cashFlow}
                startDate={startDate}
                endDate={endDate}
                activeProject={activeProject}
                accounts={accounts}
                formatCurrency={formatCurrency}
              />
            )}
          </Tab>

          <Tab eventKey="ai-insights" title="AI Insights">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <AISummaryReport 
                  report={aiReport}
                  loadingState={aiLoading}
                  triggerFn={() => triggerAiSummary('monthly_summary')}
                  logs={aiLogs}
                  consoleEndRef={consoleEndRef}
                />
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="audit-check" title="Audit Review">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <AISummaryReport 
                  report={auditReport}
                  loadingState={auditLoading}
                  triggerFn={() => triggerAiSummary('final_audit')}
                  logs={auditLogs}
                  consoleEndRef={consoleEndRef}
                />
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      )}

      {/* Share Modal */}
      <Modal show={showShareModal} onHide={() => setShowShareModal(false)} centered>
          <Modal.Header closeButton className="bg-light">
              <Modal.Title className="h5">Share Financial Brief</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4 text-center">
              {!shareData?.isRevoked ? (
                  <>
                    <div className="mb-4">
                        <i className="fas fa-shield-alt fa-3x text-success mb-3"></i>
                        <h5>Secure Link Generated</h5>
                        <p className="text-muted small">This link is protected by a 6-digit access code and expires in 30 days.</p>
                    </div>

                    {shareData && (
                        <div className="bg-light p-3 rounded border text-left mb-4">
                            <div className="mb-3">
                                <small className="text-muted text-uppercase font-weight-bold d-block mb-1">Public URL</small>
                                <code className="small d-block text-break p-2 bg-white border rounded">
                                    {`${window.location.origin}/shared/report/${shareData.token}`}
                                </code>
                            </div>
                            <div>
                                <small className="text-muted text-uppercase font-weight-bold d-block mb-1">Access Code</small>
                                <div className="h4 font-weight-bold text-primary mb-0" style={{ letterSpacing: '0.2rem' }}>
                                    {shareData.accessCode}
                                </div>
                            </div>
                        </div>
                    )}

                    <Button 
                        variant="primary" 
                        block
                        onClick={() => {
                            const text = `Hi, your financial brief for ${new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })} is ready. \n\nView it here: ${window.location.origin}/shared/report/${shareData.token} \n\nAccess Code: ${shareData.accessCode}`;
                            navigator.clipboard.writeText(text);
                            alert('Link and Access Code copied to clipboard!');
                        }}
                    >
                        <i className="fas fa-copy mr-2"></i> Copy Invitation Message
                    </Button>

                    <div className="mt-4 pt-3 border-top">
                        <Button 
                            variant="link" 
                            className="text-danger small" 
                            onClick={handleRevoke}
                            disabled={revokeLoading}
                        >
                            {revokeLoading ? 'Revoking...' : 'Revoke shared link and access code'}
                        </Button>
                    </div>
                  </>
              ) : (
                  <div className="py-4">
                      <i className="fas fa-user-slash fa-3x text-danger mb-3"></i>
                      <h5>Link Revoked</h5>
                      <p className="text-muted small">The previous link and access code are no longer valid. You can generate a new one by clicking the Share button again.</p>
                      <Button variant="primary" onClick={() => setShowShareModal(false)}>Close</Button>
                  </div>
              )}
          </Modal.Body>
      </Modal>
    </div>
  );
};

export default Reports;
